import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EntryComponent } from '../entry/entry.component';
import { CommonModule } from '@angular/common';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { isInFuture, isInGroupTimePeriod, isSameDay, isToday } from 'src/app/shared/utils/date';
import { userFoodEntry, userActivityEntry, userMobilityEntry, GroupMembership } from 'src/app/shared/models/interfaces';
import { DailyService } from '../daily.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/services/user/user.service';

@Component({
  selector: 'app-daily-overview',
  imports: [EntryComponent, CommonModule, UIComponentsModule, MatTooltipModule],
  templateUrl: './daily-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './daily-overview.component.scss',
})
export class DailyOverviewComponent implements OnInit, OnDestroy {
  selectedDate: Date | null = null;
  displayedDates: Date[] = [];

  groupMemberships: GroupMembership[] = [];

  userFoodEntries: userFoodEntry[] = [];
  userActivityEntries: userActivityEntry[] = [];
  userMobilityEntries: userMobilityEntry[] = [];

  private subscriptions: Subscription = new Subscription();

  constructor(private dailyService: DailyService, private userService: UserService, private route: ActivatedRoute) {}

  ngOnInit(): void {
    // Check for selectedDate query parameter
    const selectedDateParam = this.route.snapshot.queryParams['selectedDate'];
    if (selectedDateParam) {
      const date = new Date(selectedDateParam);
      if (!isNaN(date.getTime())) {
        this.onDateSelected(date);
      }
    }
    
    this.loadUserEntries();
    this.loadUserGroupMemberships();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadUserEntries() {
    // Subscribe to shared observables for real-time updates
    this.subscriptions.add(
      this.dailyService.userFoodEntries$.subscribe((entries) => {
        this.userFoodEntries = entries;
      })
    );

    this.subscriptions.add(
      this.dailyService.userActivityEntries$.subscribe((entries) => {
        this.userActivityEntries = entries;
      })
    );

    this.subscriptions.add(
      this.dailyService.userMobilityEntries$.subscribe((entries) => {
        this.userMobilityEntries = entries;
      })
    );

    // Load initial data
    this.dailyService.loadAllData();
  }

  loadUserGroupMemberships() {
    this.subscriptions.add(
      this.userService.getGroupsForUser().subscribe((memberships) => {
        this.groupMemberships = memberships;
      })
    );
  }

  onDatesGenerated(dates: Date[]) {
    this.displayedDates = dates;
  }

  dateClass = (date: Date): string[] => {
    const classes: string[] = [];
    if (this.hasEntries(date)) {
      classes.push('has-entries-date');
    }
    if (this.isInGroupTimePeriod(date, this.groupMemberships)) {
      classes.push('in-group-time-period');
    }
    return classes;
  };

  onDateSelected(date: Date | null) {
    if (date !== null) {
      this.selectedDate = date;
      return;
    }
    this.selectedDate = new Date();
  }

  hasEntries(date: Date): boolean {
    if (!date) return false;
    
    const hasFoodEntries = this.userFoodEntries.some(entry => 
      isSameDay(entry.occurredAt, date) && entry.nrServings > 0
    );
    
    const hasActivityEntries = this.userActivityEntries.some(entry =>
      isSameDay(entry.occurredAt, date) && entry.duration > 0
    );

    const hasMobilityEntries = this.userMobilityEntries.some(entry =>
      isSameDay(entry.occurredAt, date) && entry.distance > 0
    );

    return hasFoodEntries || hasActivityEntries || hasMobilityEntries;
  }

  onBackToOverview() {
    this.selectedDate = null;
  }
  
  // Date utility methods, declare to use the imported utility functions in the template
  isInFuture = isInFuture;
  isToday = isToday;
  isInGroupTimePeriod = isInGroupTimePeriod;
}